/**
 * North American Numbering Plan validation and display formatting.
 *
 * The API stores `primary_phone` as an unvalidated string, so anything typed here
 * is what integrations later hand to DoseSpot and Kno2. Catching a bad number at
 * the form is the difference between a corrected typo and a vendor enrollment
 * rejection weeks later.
 */

const NANP_LENGTH = 10
const COUNTRY_CODE = '1'

/**
 * Numbers that are structurally valid but never a practice's direct line.
 * Kept as an explicit set: deriving them from digit patterns (say, rejecting
 * every area code whose last two digits match) also rejects assigned codes.
 */
const TOLL_FREE_AREA_CODES = new Set(['800', '833', '844', '855', '866', '877', '888'])

export const REQUIRED_ERROR = 'Phone number is required.'
export const LENGTH_ERROR = 'Enter a 10-digit US phone number.'
export const AREA_CODE_ERROR = 'That area code is not valid.'
export const TOLL_FREE_ERROR = 'Enter a direct line rather than a toll-free number.'
export const EXCHANGE_ERROR = 'That phone number is not valid.'

/** Digits only, with a leading US country code dropped. */
export const toDigits = (value: string): string => {
  const digits = value.replace(/\D/g, '')
  return digits.length === NANP_LENGTH + 1 && digits.startsWith(COUNTRY_CODE)
    ? digits.slice(1)
    : digits
}

/** An NANP area code or central-office code is N-X-X: leading 2-9, and never N11. */
const isValidNanpCode = (code: string): boolean =>
  /^[2-9]\d\d$/.test(code) && !code.endsWith('11')

/**
 * Returns an empty string when the number is usable, or the reason it is not.
 *
 * Empty input reports as required, so a single validator covers both the
 * presence and the format rules a form field needs.
 */
export const validatePhoneNumber = (value: string): string => {
  if (!value.trim()) {
    return REQUIRED_ERROR
  }

  const digits = toDigits(value)
  if (digits.length !== NANP_LENGTH) {
    return LENGTH_ERROR
  }

  const areaCode = digits.slice(0, 3)
  if (!isValidNanpCode(areaCode)) {
    return AREA_CODE_ERROR
  }
  if (TOLL_FREE_AREA_CODES.has(areaCode)) {
    return TOLL_FREE_ERROR
  }
  if (!isValidNanpCode(digits.slice(3, 6))) {
    return EXCHANGE_ERROR
  }

  return ''
}

/**
 * Formats a 10-digit number as (555) 123-4567 for display.
 *
 * Anything that isn't ten digits comes back trimmed but otherwise untouched —
 * this is a display helper, so it must never obscure what is actually stored.
 */
export const formatPhoneNumber = (value: string): string => {
  const digits = toDigits(value)
  if (digits.length !== NANP_LENGTH) {
    return value.trim()
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}
